import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LogIn, Eye, EyeOff, ArrowLeft, Leaf } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_citrabi-tech/artifacts/oxx9leeo_%5BCITRA%5D%20Logo_No%20Background%20%281%29.png";

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading } = useAuth();
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    company_name: 'Citra Abi Permana',
    logo_url: DEFAULT_LOGO
  });

  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const response = await axios.get(`${API}/contact-settings`);
        if (response.data) {
          setCompanyInfo({
            company_name: response.data.company_name || 'Citra Abi Permana',
            logo_url: response.data.logo_url || DEFAULT_LOGO
          });
        }
      } catch (error) {
        console.error('Error fetching company settings:', error);
      }
    };
    fetchCompanySettings();
  }, []);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast.success(language === 'en' ? 'Login successful!' : 'Login berhasil!');
      navigate('/admin');
    } else {
      toast.error(result.error);
    }
    
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="admin-login-page">
      {/* Left Panel - Login Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 bg-white">
        <div className="max-w-md w-full mx-auto">
          {/* Back to Home */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-stone-600 hover:text-emerald-600 mb-8 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'en' ? 'Back to Website' : 'Kembali ke Website'}
          </Link>

          {/* Logo */}
          <div className="mb-8">
            <img src={companyInfo.logo_url} alt={companyInfo.company_name} className="h-14 w-auto" />
          </div>

          {/* Login Form */}
          <div className="mb-8">
            <h1 className="font-heading font-bold text-2xl lg:text-3xl text-stone-900 mb-2" data-testid="login-title">
              {language === 'en' ? 'Welcome Back' : 'Selamat Datang Kembali'}
            </h1>
            <p className="text-stone-600">
              {language === 'en' ? 'Sign in to access the admin dashboard' : 'Masuk untuk mengakses dashboard admin'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">{language === 'en' ? 'Email Address' : 'Alamat Email'}</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@citraabi.com"
                className="w-full"
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{language === 'en' ? 'Password' : 'Kata Sandi'}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="login-submit"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  {language === 'en' ? 'Signing in...' : 'Masuk...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="w-4 h-4" />
                  {language === 'en' ? 'Sign In' : 'Masuk'}
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>

      {/* Right Panel - Decorative */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-emerald-600 to-emerald-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 border border-white rounded-full"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 border border-white rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 w-48 h-48 border border-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        </div>
        
        <div className="relative flex flex-col justify-center items-center text-white p-16">
          <Leaf className="w-24 h-24 mb-8 opacity-80" />
          <h2 className="font-heading font-bold text-3xl text-center mb-4">
            {language === 'en' ? 'Content Management System' : 'Sistem Manajemen Konten'}
          </h2>
          <p className="text-emerald-100 text-center max-w-md">
            {language === 'en' 
              ? 'Manage your website content, services, translations, and customer inquiries from one central dashboard.'
              : 'Kelola konten website, layanan, terjemahan, dan pertanyaan pelanggan dari satu dashboard terpusat.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
